/* eslint-disable react-hooks/exhaustive-deps */
import { ProductName } from '@pypestream/api-services';
import {
  NavLink,
  NavLinkProps,
  NavLinkWC,
  NavLinks,
  UniversalNav,
  UniversalNavChangelog,
  UniversalNavHelp,
  UniversalUserDropdown,
  UniversalUserDropdownTrigger,
} from '@pypestream/design-system';
import {
  TransKeyProp,
  TranslationComponent,
  TranslationKeys,
} from '@pypestream/translations';
import { removeTrailingSlash } from '@pypestream/utils';
import React, { FC, useMemo } from 'react';
import {
  NavLinkProps as NavLinkPropsReactRouter,
  useHref,
  useLinkClickHandler,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router';

import { OrganizationRoleNames, useAuthRole, useAnalytics } from '../../hooks';
import {
  sendUserEvent,
  useGlobalAppCtxSelector,
  useManagerCtxSelector,
  useManagerStateMatchesOneOf,
} from '../../xstate/app.xstate';

type NavItem = {
  to: string;
  label: string;
  hidden: boolean;
  i18nKey?: string;
};

const parsedChangelogId = Number(
  import.meta.env.FE_PRODUKTLY_MANAGER_CHANGELOG_ID
);
const changelogId =
  Number.isFinite(parsedChangelogId) && parsedChangelogId > 0
    ? parsedChangelogId
    : undefined;

export const NavLinkWrapper = React.forwardRef<
  NavLinkProps,
  TransKeyProp &
    NavLinkPropsReactRouter & {
      onClick?: React.MouseEventHandler<NavLinkWC>;
      children?: React.ReactNode;
      isActive?: boolean;
      disabled?: boolean;
      external?: boolean;
      label?: string;
    }
>(
  (
    {
      label,
      onClick,
      replace = false,
      state,
      target,
      to,
      children,
      i18nKey,
      isActive = false,
      disabled = false,
      external = false,
    },
    ref
  ) => {
    const href = useHref(to);
    const handleClick = useLinkClickHandler<NavLinkWC>(to, {
      replace,
      state,
      target,
    });

    return (
      <NavLink
        test-id={`nav-link-${label?.toLowerCase()}`}
        active={isActive}
        href={external ? String(to) : href}
        onClick={(event: React.MouseEvent<NavLinkWC, MouseEvent>) => {
          if (!external) {
            onClick?.(event);
            if (!event.defaultPrevented && !disabled) {
              handleClick(event);
            }
          }
        }}
        ref={ref}
        target={target}
        i18nKey={i18nKey}
        disabled={disabled}
      >
        {children}
      </NavLink>
    );
  }
);

export const PrimaryNav: FC = () => {
  const featureFlags = useGlobalAppCtxSelector((ctx) => ctx.featureFlags);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The path My Account is served on (router.tsx); the org-less `/my-account`
  // is a redirect to it, so one match covers both entry points.
  //
  // Until PSTUDIO-4095 this path was a `<Navigate>` into
  // `/organization/:org_id/users/:user_id`, so by the time an org switch
  // happened the pathname was the Users one and this match was false;
  // `onSelectionChange` then fell through to `getMatchedEntity`, which finds
  // `/users/` in that pathname and navigates to the new org's Users list. With
  // a real page here the match holds and the branch below sends the switch to
  // the org's home page, as it was written to. Not exercised in the local
  // manual pass: the test account belongs to a single org, so there was no
  // second org to switch to.
  const isUserProfilePage = Boolean(
    useMatch('/organization/:org_id/my-account')
  );
  const analytics = useAnalytics();

  const shouldUpdateUser = !!useManagerStateMatchesOneOf([
    'orgRelated.ready.orgDetails.added',
    'orgRelated.ready.orgDetails.updated',
    'orgRelated.ready.orgDetails.deleted',
    'orgRelated.ready.projectDetails.reloadAfterDelay',
    'orgRelated.ready.projectDetails.projectDeleted',
  ]);

  const shouldWaitDataLoad = !!useManagerStateMatchesOneOf([
    'orgRelated.ready.projects.loading',
    'orgRelated.ready.users.loading',
    'orgRelated.ready.teams.loading',
    'orgRelated.ready.childOrgs.loading',
    'orgRelated.ready.orgs.loading',
  ]);

  const { selectedOrgId, selectedProject, routes, isCurrentUser } =
    useManagerCtxSelector((ctx) => ({
      routes: ctx.routes,
      selectedOrgId: ctx.selectedOrgId,
      selectedProject: ctx.selectedProject,
      isCurrentUser:
        !!ctx.selectedUser && ctx.selectedUser === ctx.userInfo?.id,
    }));
  const isCurrentUserUpdated =
    !!useManagerStateMatchesOneOf([
      'orgRelated.ready.userDetails.userDetailsUpdated',
    ]) && isCurrentUser;

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const pathAsArray = removeTrailingSlash(pathname).split('/').filter(Boolean);

  const navItems = useMemo(() => {
    const items: NavItem[] = [
      {
        to: routes.users,
        label: 'Users',
        hidden: false,
        i18nKey: 'manager/common:navMenu.users',
      },
      {
        to: routes.teams,
        label: 'Teams',
        hidden: false,
        i18nKey: 'manager/common:navMenu.teams',
      },
      {
        to: routes.projects,
        label: 'Projects',
        hidden: false,
        i18nKey: 'manager/common:navMenu.projects',
      },
      {
        to: routes.orgs,
        label: 'Orgs',
        hidden: false,
        i18nKey: 'manager/common:navMenu.orgs',
      },
      {
        to: routes.logs,
        label: 'Logs',
        hidden: !isAdminRole,
        i18nKey: 'manager/common:navMenu.logs',
      },
    ];

    return items.filter((item) => !item.hidden);
  }, [routes, isAdminRole]);

  const isHomePage = useMemo(
    () => pathname === '/' || pathAsArray.length === 2,
    [pathname, pathAsArray]
  );

  return (
    <UniversalNav
      app={ProductName.Organization}
      featureFlags={featureFlags}
      shouldUpdateUser={shouldUpdateUser || isCurrentUserUpdated}
      slot="nav"
      orgId={selectedOrgId}
      projectId={selectedProject}
      hideGoHome={isHomePage}
      onManagerToolClick={navigate}
      isDisabledOrgs={shouldWaitDataLoad}
      onSelectionChange={({ detail: { selection: orgId } }) => {
        if (!orgId || shouldWaitDataLoad) return;

        const entityPaths = ['users', 'projects', 'teams'];
        const currentPath = pathname;

        if (isUserProfilePage) {
          navigate(`/organization/${orgId}`);
          return;
        }

        const getMatchedEntity = (path: string, entities: string[]) =>
          entities.find((entity) => path.includes(`/${entity}/`));

        const matchedEntity = getMatchedEntity(currentPath, entityPaths);

        if (selectedOrgId && matchedEntity) {
          navigate(`/organization/${orgId}/${matchedEntity}`);
          return;
        }

        if (selectedProject) {
          navigate(`/organization/${orgId}/projects`);
          return;
        }

        if (selectedOrgId) {
          const updatedPath = currentPath.replace(selectedOrgId, orgId);
          navigate(updatedPath);
          return;
        }

        navigate(`/organization/${orgId}`);
      }}
    >
      <NavLinks slot="navigation">
        {!isHomePage && (
          <>
            {navItems.map(({ to, label, i18nKey }) => (
              <NavLinkWrapper
                label={label}
                key={label}
                to={to}
                isActive={
                  (pathname.endsWith('edit') && label === 'Orgs') ||
                  pathname.includes(to)
                }
                external={to.includes(routes.admin)}
              >
                {i18nKey ? (
                  <TranslationComponent i18nKey={i18nKey as TranslationKeys}>
                    {label}
                  </TranslationComponent>
                ) : (
                  label
                )}
              </NavLinkWrapper>
            ))}
          </>
        )}
      </NavLinks>

      {changelogId !== undefined && (
        <UniversalNavChangelog slot="actions" changelogId={changelogId} />
      )}
      <UniversalNavHelp slot="actions" />

      <UniversalUserDropdownTrigger
        slot="actions"
        test-id="user-dropdown-trigger"
      />

      <UniversalUserDropdown
        onPreferencesClick={() => navigate(routes.myAccount)}
        onLogout={async () => {
          if (analytics && typeof analytics.reset === 'function') {
            analytics.reset();
          }
          sendUserEvent('user.signOut');
        }}
      />
    </UniversalNav>
  );
};
